import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DEFAULT_PAGE_SIZE_OPTIONS } from './pagination.ts';

export type TablePaginationFooterProps = {
  from: number;
  to: number;
  total: number;
  itemLabel: string;
  /** 0-based page index */
  pageIndex: number;
  pageCount?: number;
  canPreviousPage?: boolean;
  canNextPage?: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  showPageSizeSelector?: boolean;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeSelectorLabel?: string;
  className?: string;
};

export function TablePaginationFooter({
  from,
  to,
  total,
  itemLabel,
  pageIndex,
  pageCount,
  canPreviousPage,
  canNextPage,
  onPreviousPage,
  onNextPage,
  showPageSizeSelector = false,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
  pageSizeSelectorLabel = 'Rows per page',
  className = '',
}: TablePaginationFooterProps): React.ReactElement {
  const resolvedPageCount = pageCount ?? Math.max(1, Math.ceil(total / Math.max(1, pageSize ?? 10)));
  const prevDisabled = canPreviousPage !== undefined ? !canPreviousPage : pageIndex <= 0;
  const nextDisabled =
    canNextPage !== undefined ? !canNextPage : pageIndex >= resolvedPageCount - 1;

  return (
    <div
      className={`flex items-center justify-between flex-wrap gap-3 px-6 pb-4 pt-4 border-t border-gray-200 ${className}`.trim()}
    >
      <p className="text-sm text-gray-500">
        Showing {from} to {to} of {total} {itemLabel}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {showPageSizeSelector && pageSize != null && onPageSizeChange ? (
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span className="whitespace-nowrap">{pageSizeSelectorLabel}</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label={pageSizeSelectorLabel}
              className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 shadow-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={prevDisabled}
            onClick={onPreviousPage}
            aria-label="Previous page"
            className="inline-flex items-center justify-center h-8 w-8 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-300"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <span className="text-sm text-gray-600 px-2">
            Page {pageIndex + 1} of {resolvedPageCount}
          </span>
          <button
            type="button"
            disabled={nextDisabled}
            onClick={onNextPage}
            aria-label="Next page"
            className="inline-flex items-center justify-center h-8 w-8 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-300"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
